import { PlusIcon } from "@heroicons/react/24/outline";
import { ReferenceBusinessId, ReferenceContactId, RegionId } from "@sp-crm/core";
import { useQueryClient } from "@tanstack/react-query";
import { NewInlineOrganization } from "components/references/select-reference/new-inline-organization";
import { NewInlineReference } from "components/references/select-reference/new-inline-reference";
import { trpc, trpcClient } from "components/rpc/trpc";
import { BuildingOfficePlusIcon } from "components/ui/building-office-plus-icon";
import { fancyAlert } from "components/ui/fancy-alert";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    useCreateReferenceBusinessMutation,
    useCreateReferenceContactMutation,
} from "generated/graphql";
import React from "react";
import { useQueryClient as useGraphqlQueryClient } from "react-query";
import { handleEvent } from "util/user-events";
import { BoardReferralSourcePicker } from "./board-referral-source-picker";
import { PipelineEntityRef, PipelineStageSummary, PipelineSummary } from "./types";

type AddPanelMode =
    | { kind: "search" }
    | { kind: "newContact"; referenceContactId: ReferenceContactId | null }
    | { kind: "newOrganization"; referenceBusinessId: ReferenceBusinessId | null };

interface AddReferralSourcePanelProps {
    panelTitle: string;
    stage: PipelineStageSummary;
    regionId: RegionId;
    isOpen: boolean;
    onClose: () => void;
}

const AddReferralSourcePanel: React.FC<AddReferralSourcePanelProps> = props => {
    const { panelTitle, stage, regionId, isOpen, onClose } = props;
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    const graphqlQueryClient = useGraphqlQueryClient();
    const [mode, setMode] = React.useState<AddPanelMode>({ kind: "search" });
    const createContactMutation = useCreateReferenceContactMutation();
    const createBusinessMutation = useCreateReferenceBusinessMutation();

    const refetchStage = React.useCallback(async () => {
        await queryClient.refetchQueries({
            queryKey: trpcContext.pipeline.boardStage.queryKey({
                stageId: stage.id,
                regionId,
            }),
        });
    }, [queryClient, trpcContext, stage.id, regionId]);

    const handleDismiss = React.useCallback(async () => {
        graphqlQueryClient.invalidateQueries("getReferenceContact");
        graphqlQueryClient.invalidateQueries("getReferenceBusiness");
        setMode({ kind: "search" });
        onClose();
        await refetchStage();
    }, [graphqlQueryClient, setMode, onClose, refetchStage]);

    const addEntityToBoard = React.useCallback(
        async (entity: PipelineEntityRef) => {
            handleEvent("referral-pipeline-add-card", { source: "pipeline" });
            try {
                const memberships = await trpcClient.pipeline.entityStages.query({
                    entityType: entity.entityType,
                    entityId: entity.entityId,
                });
                const existing = memberships.find(
                    membership => membership.pipelineId === stage.pipelineId,
                );
                if (existing) {
                    if (existing.pipelineStageId !== stage.id) {
                        await trpcClient.pipeline.moveCard.mutate({
                            membershipId: existing.id,
                            stageId: stage.id,
                        });
                    }
                } else {
                    await trpcClient.pipeline.addCard.mutate({
                        stageId: stage.id,
                        entityType: entity.entityType,
                        entityId: entity.entityId,
                    });
                }
            } catch (error) {
                await fancyAlert(
                    "Unable to add",
                    error instanceof Error
                        ? error.message
                        : "Unable to add this referral source",
                );
            } finally {
                await refetchStage();
            }
        },
        [stage.id, stage.pipelineId, refetchStage],
    );

    const handleNewContact = React.useCallback(async () => {
        setMode({ kind: "newContact", referenceContactId: null });
        const response = await createContactMutation.mutateAsync({ regionId });
        const referenceContactId = response?.createReferenceContact?.id ?? null;
        if (!referenceContactId) {
            setMode({ kind: "search" });
            return;
        }
        setMode({ kind: "newContact", referenceContactId });
        await addEntityToBoard({
            entityType: "ReferenceContact",
            entityId: referenceContactId,
        });
    }, [setMode, createContactMutation, regionId, addEntityToBoard]);

    const handleNewOrganization = React.useCallback(async () => {
        setMode({ kind: "newOrganization", referenceBusinessId: null });
        const response = await createBusinessMutation.mutateAsync({ regionId });
        const referenceBusinessId = response?.createReferenceBusiness?.id ?? null;
        if (!referenceBusinessId) {
            setMode({ kind: "search" });
            return;
        }
        setMode({ kind: "newOrganization", referenceBusinessId });
        await addEntityToBoard({
            entityType: "ReferenceBusiness",
            entityId: referenceBusinessId,
        });
    }, [setMode, createBusinessMutation, regionId, addEntityToBoard]);

    let headerText = panelTitle;
    if (mode.kind === "newContact") {
        headerText = "New referral contact";
    } else if (mode.kind === "newOrganization") {
        headerText = "New referral organization";
    }

    return (
        <Panel
            isOpen={isOpen}
            headerText={headerText}
            type={PanelType.extraLarge}
            onDismiss={handleDismiss}>
            {mode.kind === "newContact" ? (
                <NewInlineReference
                    referenceContactId={mode.referenceContactId}
                    dismiss={handleDismiss}
                />
            ) : mode.kind === "newOrganization" ? (
                <NewInlineOrganization
                    referenceBusinessId={mode.referenceBusinessId}
                    dismiss={handleDismiss}
                />
            ) : (
                <div className="space-y-1 lg:space-y-2">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <SecondaryButton onClick={handleNewContact}>
                            <div className="flex items-center space-x-2">
                                <PlusIcon className="w-5 h-5" />
                                <span className="text-sm">New contact</span>
                            </div>
                        </SecondaryButton>
                        <SecondaryButton onClick={handleNewOrganization}>
                            <div className="flex items-center space-x-2">
                                <BuildingOfficePlusIcon className="w-5 h-5" />
                                <span className="text-sm">New organization</span>
                            </div>
                        </SecondaryButton>
                    </div>
                    <BoardReferralSourcePicker
                        stage={stage}
                        regionId={regionId}
                        onAdded={refetchStage}
                    />
                </div>
            )}
        </Panel>
    );
};

interface AddReferralToBoardButtonProps {
    pipeline: PipelineSummary;
    regionId: RegionId;
}

export const AddReferralToBoardButton: React.FC<
    AddReferralToBoardButtonProps
> = props => {
    const { pipeline, regionId } = props;
    const [isOpen, setIsOpen] = React.useState(false);

    const firstStage = pipeline.stages.length > 0 ? pipeline.stages[0] : null;

    const handleOpen = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setIsOpen(true);
        },
        [setIsOpen],
    );

    const handleClose = React.useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    if (!firstStage) {
        return null;
    }

    return (
        <>
            <PrimaryButton onClick={handleOpen}>
                <div className="flex items-center space-x-2">
                    <PlusIcon className="w-5 h-5" />
                    <span className="text-sm">Add referral source</span>
                </div>
            </PrimaryButton>
            <AddReferralSourcePanel
                panelTitle={`Add to ${pipeline.name}`}
                stage={firstStage}
                regionId={regionId}
                isOpen={isOpen}
                onClose={handleClose}
            />
        </>
    );
};

interface AddCardToStageButtonProps {
    stage: PipelineStageSummary;
    regionId: RegionId;
}

export const AddCardToStageButton: React.FC<AddCardToStageButtonProps> = props => {
    const { stage, regionId } = props;
    const [isOpen, setIsOpen] = React.useState(false);

    const handleOpen = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setIsOpen(true);
        },
        [setIsOpen],
    );

    const handleClose = React.useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    return (
        <>
            <InteractiveTextLink onClick={handleOpen} className="text-sm">
                <div className="flex items-center space-x-1">
                    <PlusIcon className="w-4 h-4" />
                    <span>Add</span>
                </div>
            </InteractiveTextLink>
            <AddReferralSourcePanel
                panelTitle={`Add to ${stage.name}`}
                stage={stage}
                regionId={regionId}
                isOpen={isOpen}
                onClose={handleClose}
            />
        </>
    );
};
